import * as XLSX from 'xlsx-js-style';
import { validateChequeNumber } from '../../utils/chequeValidators';

export const chequeImportService = {
  generateTemplate: () => {
    const templateData = [
      {
        'Beneficiary Name': 'John Smith',
        'Amount': 5000,
        'Cheque Date': '2026-03-01',
        'Cheque Number': '000001',
        'Remarks': 'Monthly payment'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData, {
      header: Object.keys(templateData[0])
    });

    const instructions = [
      { Field: 'Beneficiary Name', Description: 'Name of the payee (Required)', Example: 'John Smith', 'Max Length': '200 characters' },
      { Field: 'Amount', Description: 'Cheque amount, must be greater than 0 (Required)', Example: '5000', 'Max Length': '' },
      { Field: 'Cheque Date', Description: 'Date of the cheque (Required). Accepts YYYY-MM-DD or DD-MM-YYYY', Example: '2026-03-01', 'Max Length': '' },
      { Field: 'Cheque Number', Description: 'Numeric cheque number (Required)', Example: '000001', 'Max Length': '6 digits' },
      { Field: 'Remarks', Description: 'Additional notes (Optional)', Example: 'Monthly rent', 'Max Length': '500 characters' }
    ];

    const instructionsWs = XLSX.utils.json_to_sheet(instructions);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
    XLSX.utils.book_append_sheet(wb, ws, 'Cheque Template');

    ws['!cols'] = [
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 }
    ];

    instructionsWs['!cols'] = [
      { wch: 20 },
      { wch: 55 },
      { wch: 20 },
      { wch: 15 }
    ];

    return wb;
  },

  downloadTemplate: () => {
    const wb = chequeImportService.generateTemplate();
    const fileName = `Cheque_Import_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  },

  validateFileStructure: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          if (!workbook.SheetNames.includes('Cheque Template')) {
            reject(new Error('Excel file must contain a "Cheque Template" sheet'));
            return;
          }

          const worksheet = workbook.Sheets['Cheque Template'];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            reject(new Error('Excel file is empty'));
            return;
          }

          const requiredFields = ['Beneficiary Name', 'Amount', 'Cheque Date'];
          const firstRow = jsonData[0];
          const missingFields = requiredFields.filter(field => !(field in firstRow));

          if (missingFields.length > 0) {
            reject(new Error(`Missing required columns: ${missingFields.join(', ')}`));
            return;
          }

          resolve({
            isValid: true,
            data: jsonData,
            rowCount: jsonData.length
          });
        } catch (error) {
          reject(new Error('Invalid Excel file format'));
        }
      };
      reader.readAsArrayBuffer(file);
    });
  },

  parseAndValidateRows: (rawData) => {
    return rawData.map((row, index) => {
      const errors = [];
      const rowNumber = index + 1;

      // Validate beneficiary name
      const beneficiaryName = row['Beneficiary Name'] ? String(row['Beneficiary Name']).trim() : '';
      if (!beneficiaryName) {
        errors.push('Beneficiary Name is required');
      } else if (beneficiaryName.length > 200) {
        errors.push('Beneficiary Name must be 200 characters or less');
      }

      // Validate amount
      const rawAmount = row['Amount'];
      const chqAmount = parseFloat(rawAmount);
      if (rawAmount === undefined || rawAmount === null || rawAmount === '') {
        errors.push('Amount is required');
      } else if (isNaN(chqAmount) || chqAmount <= 0) {
        errors.push('Amount must be a number greater than 0');
      }

      // Validate cheque date
      let chqDate = '';
      const rawDate = row['Cheque Date'];
      if (!rawDate) {
        errors.push('Cheque Date is required');
      } else {
        const dateStr = String(rawDate).trim();
        // Try YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const parsed = new Date(dateStr);
          if (isNaN(parsed.getTime())) {
            errors.push('Invalid date format');
          } else {
            chqDate = dateStr;
          }
        }
        // Try DD-MM-YYYY format
        else if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
          const parts = dateStr.split('-');
          const parsed = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          if (isNaN(parsed.getTime())) {
            errors.push('Invalid date format');
          } else {
            chqDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
          }
        }
        // Try Excel serial date number
        else if (!isNaN(Number(dateStr))) {
          const excelDate = XLSX.SSF.parse_date_code(Number(dateStr));
          if (excelDate) {
            const y = excelDate.y;
            const m = String(excelDate.m).padStart(2, '0');
            const d = String(excelDate.d).padStart(2, '0');
            chqDate = `${y}-${m}-${d}`;
          } else {
            errors.push('Invalid date format. Use YYYY-MM-DD or DD-MM-YYYY');
          }
        }
        else {
          errors.push('Invalid date format. Use YYYY-MM-DD or DD-MM-YYYY');
        }
      }

      // Validate cheque number (required)
      let chequeNumber = '';
      const rawChequeNumber = row['Cheque Number'];
      if (rawChequeNumber === undefined || rawChequeNumber === null || String(rawChequeNumber).trim() === '') {
        errors.push('Cheque Number is required');
      } else {
        chequeNumber = String(rawChequeNumber).trim();
        const validation = validateChequeNumber(chequeNumber);
        if (!validation.isValid) {
          errors.push(validation.message);
        }
      }

      // Validate remarks (optional)
      const remarks = row['Remarks'] ? String(row['Remarks']).trim() : '';
      if (remarks.length > 500) {
        errors.push('Remarks must be 500 characters or less');
      }

      return {
        rowNumber,
        data: row,
        isValid: errors.length === 0,
        errors,
        parsed: {
          beneficiaryName,
          chqAmount: isNaN(chqAmount) ? 0 : chqAmount,
          chqDate,
          chequeNumber,
          remarks
        }
      };
    });
  },

  generateResultFile: (results) => {
    const resultData = results.map(result => ({
      'Row Number': result.rowNumber,
      'Beneficiary Name': result.data['Beneficiary Name'] || '',
      'Amount': result.data['Amount'] || '',
      'Cheque Date': result.data['Cheque Date'] || '',
      'Cheque Number': result.data['Cheque Number'] || '',
      'Remarks': result.data['Remarks'] || '',
      'Validation Status': result.isValid ? 'VALID' : 'INVALID',
      'Errors': result.errors.join('; ')
    }));

    const ws = XLSX.utils.json_to_sheet(resultData);
    ws['!cols'] = [
      { wch: 12 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 },
      { wch: 18 },
      { wch: 50 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Validation Results');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T');
    const fileName = `Cheque_Import_Validation_${timestamp[0]}_${timestamp[1].split('.')[0]}.xlsx`;

    return { workbook: wb, fileName };
  },

  downloadResultFile: (results) => {
    const { workbook, fileName } = chequeImportService.generateResultFile(results);
    XLSX.writeFile(workbook, fileName);
  }
};
